// Page du prestataire simulé (développement et présentation seulement) : montant, bénéficiaire et
// état lus par la route publique, puis confirmation, refus ou expiration qui envoient au serveur
// une notification signée traitée comme celle d'un vrai prestataire. Aucun argent réel n'est
// échangé ; en production la route n'existe pas et la page le dit.
import { Bandeau, Bouton, EtatVide, LienBouton } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ErreurApi, messageErreur } from '../../../api/erreurs';
import { AffichageErreur } from '../../../commun/EtatRequete';
import { Bloc, EnTeteCitoyen, TexteStatut } from '../commun';
import { agirSimulateur, usePageSimulateur } from './api';
import { libelleEtatPaiement, montant, tonEtatPaiement } from './logique';
import type { ResultatSimulateur } from './types';
import styles from '../citoyen.module.css';

const LIBELLES_ACTION: Record<string, string> = { confirmer: 'Confirmer le paiement', refuser: 'Refuser le paiement', expirer: 'Laisser expirer' };
const LIBELLES_TRAITEMENT: Record<string, string> = {
  appliquee: 'Notification appliquée par la collectivité',
  ignoree_doublon: 'Notification déjà reçue : ignorée (aucun effet)',
  tardive: 'Notification tardive : signalée en anomalie, créance inchangée',
  contradictoire: 'Notification contradictoire : signalée en anomalie, créance inchangée',
  invalide: 'Notification invalide : rejetée',
};

export function PageSimulateurPaiement() {
  const { reference = '' } = useParams();
  const page = usePageSimulateur(reference || null);
  const [resultat, setResultat] = useState<ResultatSimulateur | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const agir = (action: string) => {
    setEnCours(action);
    setErreur(null);
    agirSimulateur(reference, action)
      .then((r) => {
        setResultat(r);
        void page.refetch();
      })
      .catch((e: unknown) => setErreur(messageErreur(e)))
      .finally(() => setEnCours(null));
  };
  const d = page.data;
  return (
    <>
      <EnTeteCitoyen titre="Simulateur de paiement" surTitre={reference} retour={`/citoyen/paiements/suivi/${reference}`} libelleRetour="Suivi du paiement" />
      {page.isPending ? <p className={styles.aide}>Lecture de la transaction…</p> : null}
      {page.error instanceof ErreurApi && page.error.statut === 404 ? (
        <EtatVide titre="Simulateur indisponible" niveau={2}>
          <p>Aucun simulateur de paiement n&apos;est configuré sur ce serveur (il est toujours absent en production) ou la référence est inconnue. Suivez votre paiement depuis la rubrique Paiements.</p>
        </EtatVide>
      ) : page.error ? (
        <AffichageErreur erreur={page.error} onReessayer={() => void page.refetch()} fonction="Le simulateur" />
      ) : null}
      {d ? (
        <>
          <Bandeau type="attention" titre="Présentation">
            {d.mention}.
          </Bandeau>
          <Bloc etiquette={`Bénéficiaire : ${d.beneficiaire}`} titre={`${montant(d.montant)}`}>
            <p className={styles.blocTexte}>
              État du paiement : <TexteStatut libelle={libelleEtatPaiement(d.etat)} ton={tonEtatPaiement(d.etat)} />
            </p>
            <div className={styles.ligneBoutons} role="group" aria-label="Réponse du prestataire simulé">
              {(['confirmer', 'refuser', 'expirer'] as const)
                .filter((a) => d.actions.includes(a))
                .map((a) => (
                  <Bouton key={a} variante={a === 'confirmer' ? 'principal' : 'secondaire'} chargement={enCours === a} disabled={enCours !== null} onClick={() => agir(a)}>
                    {LIBELLES_ACTION[a]}
                  </Bouton>
                ))}
            </div>
            <p className={styles.aide}>Chaque bouton envoie à la collectivité une notification signée, comme le ferait un vrai prestataire ; une confirmation envoyée deux fois est ignorée.</p>
          </Bloc>
          {resultat ? (
            <Bandeau type={resultat.traitement === 'appliquee' ? 'succes' : 'info'} titre={LIBELLES_TRAITEMENT[resultat.traitement] ?? resultat.traitement}>
              Paiement {resultat.etat ? libelleEtatPaiement(resultat.etat).toLowerCase() : 'inchangé'}.{resultat.detail ? ` ${resultat.detail}` : ''}
            </Bandeau>
          ) : null}
          {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
          <div className={styles.ligneBoutons}>
            <LienBouton href={`/citoyen/paiements/suivi/${reference}`} variante={resultat ? 'principal' : 'secondaire'}>
              Retour au suivi du paiement
            </LienBouton>
          </div>
        </>
      ) : null}
    </>
  );
}
